import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Calendar, User, MoreHorizontal, Edit, Trash2, Clock, CheckCircle, Circle, PlayCircle, MessageCircle } from 'lucide-react';
import { Task, Project } from '@/types/product-manager';
import { updateTask, deleteTask } from '@/services/productManagerService';
import { Timestamp } from 'firebase/firestore';
import { useAuth } from '@/context/AuthContext';
import ConfirmDialog from './ConfirmDialog';
import EditTaskDialog from './EditTaskDialog';
import { productManagerEmailService } from '@/services/productManagerEmailService';

interface TaskCardProps {
  task: Task;
  project?: Project;
  projects?: Project[];
  onUpdate: () => void;
  viewMode: 'card' | 'list';
}

const TaskCard: React.FC<TaskCardProps> = ({ task, project, projects = [], onUpdate, viewMode }) => {
  const navigate = useNavigate();
  const { userEmail } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'high': return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'low': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };


  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'todo': return Circle;
      case 'in-progress': return PlayCircle;
      case 'completed': return CheckCircle;
      default: return Circle;
    }
  };

  const handleStatusChange = async (newStatus: 'todo' | 'in-progress' | 'completed') => {
    try {
      setLoading(true);
      const updates: Partial<Task> = {
        status: newStatus,
        updatedAt: Timestamp.now() as any
      };

      // Only add completedAt if moving to completed status
      if (newStatus === 'completed' && task.status !== 'completed') {
        updates.completedAt = Timestamp.now() as any;
      }
      // Don't set completedAt to undefined - just omit it for non-completed tasks
      // Firestore doesn't like undefined values

      console.log('Updating task status:', { taskId: task.id, newStatus, updates });
      await updateTask(task.id, updates, userEmail || undefined);
      
      // Send email notification if task is assigned and status actually changed
      if (task.assignedTo && task.status !== newStatus && userEmail) {
        try {
          const statusDisplayName = newStatus.replace('-', ' ');
          await productManagerEmailService.sendTaskStatusChangedNotification(
            task.assignedTo,
            task.id,
            task.title,
            project?.name || 'Unknown Project',
            userEmail,
            statusDisplayName
          );
        } catch (emailError) {
          console.error('Error sending status change email notification:', emailError);
          // Don't fail the status update if email fails
        }
      }
      
      onUpdate();
    } catch (error) {
      console.error('Error updating task status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    console.log('Delete confirm clicked for task:', task.id);
    setShowDeleteConfirm(false);
    
    try {
      console.log('Starting delete operation...');
      await deleteTask(task.id, userEmail || undefined);
      console.log('Delete operation completed, calling onUpdate...');
      onUpdate();
      console.log('onUpdate called successfully');
    } catch (error) {
      console.error('Error deleting task:', error);
      alert('Failed to delete task. Please try again.');
    }
  };

  const handleTaskClick = (e: React.MouseEvent) => {
    // Don't navigate if clicking on buttons, dropdown menus, interactive elements, or modal content
    if ((e.target as HTMLElement).closest('button, [role="button"], [role="menuitem"], [data-radix-collection-item], [role="dialog"], [data-radix-dialog-content], .dialog-content, [aria-modal="true"]')) {
      return;
    }
    
    // Don't navigate if a modal is currently open
    if (showEditDialog || showDeleteConfirm) {
      return;
    }
    
    navigate(`/product-manager/tasks/${task.id}`);
  };

  const StatusIcon = getStatusIcon(task.status);

  const formatDate = (timestamp: any) => {
    if (!timestamp) return null;
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString();
  };

  const isOverdue = task.dueDate && task.status !== 'completed' &&
    (task.dueDate.toDate ? task.dueDate.toDate() : new Date(task.dueDate as any)) < new Date();


  return (
    <>
      <Card className="hover:shadow-md transition-shadow cursor-pointer bg-gray-100 dark:bg-gray-700 border-gray-400 dark:border-gray-600 shadow-md" onClick={handleTaskClick}>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-3 flex-1">
              <StatusIcon className={`h-6 w-6 mt-1 ${
                task.status === 'completed' ? 'text-green-600' :
                task.status === 'in-progress' ? 'text-blue-600' : 'text-gray-400'
              }`} />
              <div className="flex-1">
                <div className="mb-2">
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Task Name</p>
                  <CardTitle className={`text-lg ${
                    task.status === 'completed' ? 'line-through text-gray-500' : 'text-gray-900 dark:text-white'
                  }`}>
                    {task.title}
                  </CardTitle>
                </div>
                {project && (
                  <div className="mb-2">
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Project</p>
                    <Badge variant="outline" className="text-xs mt-1">
                      {project.name}
                    </Badge>
                  </div>
                )}
              </div>
            </div>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" disabled={loading}>
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {task.status !== 'todo' && (
                  <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleStatusChange('todo'); }}>
                    Mark as To Do
                  </DropdownMenuItem>
                )}
                {task.status !== 'in-progress' && (
                  <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleStatusChange('in-progress'); }}>
                    Mark as In Progress
                  </DropdownMenuItem>
                )}
                {task.status !== 'completed' && (
                  <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleStatusChange('completed'); }}>
                    Mark as Completed
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/product-manager/tasks/${task.id}`); }}>
                  <MessageCircle className="h-4 w-4 mr-2" />
                  View Comments
                </DropdownMenuItem>
                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setShowEditDialog(true); }}>
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setShowDeleteConfirm(true); }} className="text-red-600">
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-3">
          {task.description && (
            <div>
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Description</p>
              <CardDescription className="text-sm whitespace-pre-wrap">
                {task.description}
              </CardDescription>
            </div>
          )}
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Priority</p>
              <Badge className={getPriorityColor(task.priority)}>
                {task.priority}
              </Badge>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Tags</p>
              {task.tags && task.tags.length > 0 ? (
                <div className="flex flex-wrap gap-1">
                  {task.tags.slice(0, 2).map((tag, index) => (
                    <Badge key={index} variant="secondary" className="text-xs">
                      {tag}
                    </Badge>
                  ))}
                  {task.tags.length > 2 && (
                    <Badge variant="secondary" className="text-xs">
                      +{task.tags.length - 2}
                    </Badge>
                  )}
                </div>
              ) : (
                <span className="text-xs text-gray-400">No tags</span>
              )}
            </div>
          </div>
          
          <div className="grid grid-cols-1 gap-2">
            {task.assignedTo && (
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Assigned To</p>
                <div className="flex items-center text-sm">
                  <User className="h-4 w-4 mr-2 text-gray-500" />
                  {task.assignedTo}
                </div>
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-4">
              {task.dueDate && (
                <div>
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Due Date</p>
                  <div className={`flex items-center text-sm ${isOverdue ? 'text-red-500' : 'text-gray-600 dark:text-gray-300'}`}>
                    <Calendar className="h-4 w-4 mr-2" />
                    {formatDate(task.dueDate)}
                    {isOverdue && <span className="ml-1 text-xs">(Overdue)</span>}
                  </div>
                </div>
              )}
              
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Created At</p>
                <div className="flex items-center text-sm text-gray-600 dark:text-gray-300">
                  <Clock className="h-4 w-4 mr-2" />
                  {formatDate(task.createdAt)}
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Delete Confirmation Dialog - Outside of Card */}
      <ConfirmDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        title="Delete Task"
        description={`Are you sure you want to delete "${task.title}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="destructive"
        onConfirm={handleDeleteConfirm}
      />

      {/* Edit Task Dialog - Outside of Card */}
      <EditTaskDialog
        open={showEditDialog}
        onOpenChange={setShowEditDialog}
        task={task}
        projects={projects.length > 0 ? projects : (project ? [project] : [])}
        onTaskUpdated={onUpdate}
      />
    </>
  );
};

export default TaskCard;